/**
 * TODO:
 *    * (Ongoing) Continue to re-organize symbols / files
 *    * Address Refactor Groups (Labelled with CPR)
 *        * (GeneralRefactoring) Moving code around
 *    * (BackendExpansion) Move Expansion to Backend, and support filter/sort
 */

import {Tooltip} from '@mui/material';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridEventListener,
  GridFilterModel,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {Button} from '@wandb/weave/components/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox/Checkbox';
import {ErrorPanel} from '@wandb/weave/components/ErrorPanel';
import {
  IconBaselineAlt,
  IconNotVisible,
  IconPinToRight,
  IconSortAscending,
  IconSortDescending,
  IconTable,
} from '@wandb/weave/components/Icon';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useHistory} from 'react-router-dom';

import {useViewerInfo} from '../../../../../../common/hooks/useViewerInfo';
import {RemovableTag} from '../../../../../Tag';
import {RemoveAction} from '../../../../../Tag/RemoveAction';
import {TailwindContents} from '../../../../../Tailwind';
import {TableRowSelectionContext} from '../../../TableRowSelectionContext';
import {CallsCharts} from '../../charts/CallsCharts';
import {
  useWeaveflowCurrentRouteContext,
  WeaveflowPeekContext,
} from '../../context';
import {usePeekLocation} from '../../context';
import {AddToDatasetDrawer} from '../../datasets/AddToDatasetDrawer';
import {
  convertFeedbackFieldToBackendFilter,
  parseFeedbackType,
} from '../../feedback/HumanFeedback/tsHumanFeedback';
import {OnUpdateFilter} from '../../filters/CellFilterWrapper';
import {getDefaultOperatorForValue} from '../../filters/common';
import {FilterPanel} from '../../filters/FilterPanel';
import {getNextFilterId} from '../../filters/filterUtils';
import {flattenObjectPreservingWeaveTypes} from '../../flattenObject';
import {DEFAULT_PAGE_SIZE} from '../../grid/pagination';
import {StyledDataGrid} from '../../StyledDataGrid';
import {ConfirmDeleteModal} from '../CallPage/OverflowMenu';
import {ObjectVersionSelector} from '../common/ObjectVersionSelector';
import {FilterLayoutTemplate} from '../common/SimpleFilterableDataTable';
import {prepareFlattenedDataForTable} from '../common/tabularListViews/columnBuilder';
import {TreeBuildingCache} from '../common/tabularListViews/columnBuilderWithDepth';
import {useControllableState, useURLSearchParamsDict} from '../util';
import {ChartConfig} from '../wfReactInterface/generatedBuiltinObjectClasses.zod';
import {TraceCallSchema} from '../wfReactInterface/traceServerClientTypes';
import {traceCallToUICallSchema} from '../wfReactInterface/tsDataModelHooks';
import {EXPANDED_REF_REF_KEY} from '../wfReactInterface/tsDataModelHooksCallRefExpansion';
import {objectVersionNiceString} from '../wfReactInterface/utilities';
import {CallSchema} from '../wfReactInterface/wfDataModelHooksInterface';
import {CallsCustomColumnMenu} from './CallsCustomColumnMenu';
import {CallsSummaryProvider} from './CallsSummaryContext';
import {
  BulkAddToDatasetButton,
  BulkDeleteButton,
  CompareEvaluationsTableButton,
  CompareTracesTableButton,
  ExportSelector,
  FilterSelectedButton,
  PaginationButtons,
  RefreshButton,
} from './CallsTableButtons';
import {useCallsTableColumns} from './callsTableColumns';
import {
  ALL_TRACES_OR_CALLS_REF_KEY,
  getEffectiveFilter,
  useInputObjectVersionOptions,
  useOpVersionOptions,
  useOutputObjectVersionOptions,
  WFHighLevelCallFilter,
} from './callsTableFilter';
import {CallsTableNoRowsOverlay} from './CallsTableNoRowsOverlay';
import {DEFAULT_FILTER_CALLS, useCallsForQuery} from './callsTableQuery';
import {useCurrentFilterIsEvaluationsFilter} from './evaluationsFilter';
import {ManageColumnsButton} from './ManageColumnsButton';
import {OpSelector} from './OpSelector';
import {ParentFilterTag} from './ParentFilterTag';
import {ResizableHandle} from './ResizableHandle';

const MAX_SELECT = 100;
const MAX_COLUMNS = 1000;

const TABLE_MIN_WIDTH_PX = 200; // Minimum width for the table section
const CHARTS_MIN_WIDTH_PX = 400; // Minimum width for the charts section
// Visual width of the vertical resize handle between table and charts
const RESIZE_HANDLE_WIDTH_PX = 3;
// How much space to leave on the right for charts when dragging the handle.
// Must be at least the charts min width plus the handle width to avoid overlap/hidden handle.
const RESIZABLE_HANDLE_MAX_WIDTH_OFFSET_PX =
  CHARTS_MIN_WIDTH_PX + RESIZE_HANDLE_WIDTH_PX;
const SPLIT_VIEW_CONTAINER_MIN_HEIGHT_PX = 400; // Minimum height for the split view container
const SPLIT_VIEW_CONTAINER_HEIGHT_OFFSET_PX = 148; // Height offset for the split view container (reduced by 12px for smaller footer)
const DEFAULT_CHARTS_WIDTH_PX = 500; // Default charts width when peek is closed
const DEFAULT_TABLE_WIDTH_WHEN_PEEK_OPEN_PX = 340; // Default table width when peek is open

export const DEFAULT_HIDDEN_COLUMN_PREFIXES = [
  'attributes.weave',
  'summary.weave.feedback',
  'summary.status_counts',
  'summary.views',
  // attributes.python was logged for a short period of time
  // accidentally in v0.51.47. We can hide it for a while
  // and remove this after a few months (say Sept 2025)
  'attributes.python',
  'wb_run_id',
  'wb_run_step',
  'attributes.otel_span',
];

// Field name constants
export const CUSTOM_CHECKBOX_FIELD = 'CustomCheckbox';
export const TRACE_NAME_FIELD = 'summary.weave.trace_name';

export const ALWAYS_PIN_LEFT_CALLS = [CUSTOM_CHECKBOX_FIELD];

// Columns that should not show pin buttons in the UI (always pinned)
const COLUMNS_ALWAYS_PINNED_LEFT = [CUSTOM_CHECKBOX_FIELD, TRACE_NAME_FIELD];

export const DEFAULT_EXPAND_CALLS = ['inputs.example'];

export const DEFAULT_PIN_CALLS: GridPinnedColumnFields = {
  left: [CUSTOM_CHECKBOX_FIELD, TRACE_NAME_FIELD],
};

export const DEFAULT_SORT_CALLS: GridSortModel = [
  {field: 'started_at', sort: 'desc'},
];

export const filterHasCalledAfterDateFilter = (filter: GridFilterModel) => {
  return filter.items.some(
    item => item.field === 'started_at' && item.operator === '(date): after'
  );
};

export const DEFAULT_PAGINATION_CALLS: GridPaginationModel = {
  pageSize: DEFAULT_PAGE_SIZE,
  page: 0,
};

const CustomLoadingOverlay: React.FC = () => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
      <WaveLoader size="huge" />
    </div>
  );
};

export const CallsTable: FC<{
  entity: string;
  project: string;
  frozenFilter?: WFHighLevelCallFilter;
  initialFilter?: WFHighLevelCallFilter;
  // Setting this will make the component a controlled component. The parent
  // is responsible for updating the filter.
  onFilterUpdate?: (filter: WFHighLevelCallFilter) => void;

  hideControls?: boolean; // Hide the entire filter and column bar
  hideOpSelector?: boolean; // Hide the op selector control
  hideMetrics?: boolean; // Hide the metrics control

  columnVisibilityModel?: GridColumnVisibilityModel;
  setColumnVisibilityModel?: (newModel: GridColumnVisibilityModel) => void;

  pinModel?: GridPinnedColumnFields;
  setPinModel?: (newModel: GridPinnedColumnFields) => void;

  filterModel?: GridFilterModel;
  setFilterModel?: (newModel: GridFilterModel) => void;

  sortModel?: GridSortModel;
  setSortModel?: (newModel: GridSortModel) => void;

  paginationModel?: GridPaginationModel;
  setPaginationModel?: (newModel: GridPaginationModel) => void;

  expandRefCols?: Set<string>;
  setExpandRefCols?: (newModel: Set<string>) => void;

  // Can include glob for prefix match, e.g. "inputs.*"
  allowedColumnPatterns?: string[];

  // Column prefixes to hide by default
  defaultHiddenColumnPrefixes?: string[];

  currentViewId?: string;

  charts?: ChartConfig[] | null;
  setCharts?: (newCharts: ChartConfig[] | null) => void;

  chartsOpen?: boolean;
  setChartsOpen?: (open: boolean) => void;

  datasetSelector?: string | null;
  setDatasetSelector?: (selector: string | null) => void;

  evaluationSelector?: string | null;
  setEvaluationSelector?: (selector: string | null) => void;

  // Event handlers
  onCellClick?: GridEventListener<'cellClick'>;
  // Additional columns to disable from filtering (beyond global UNFILTERABLE_FIELDS)
  disabledFilterColumns?: string[];
  // Optional controlled selection: highlight these rows if present (empty array clears selection)
  selectedRowIds?: string[];
  // Initial header depth for nested columns
  initialHeaderDepth?: number;
}> = ({
  entity,
  project,
  initialFilter,
  onFilterUpdate,
  frozenFilter,
  hideControls,
  hideOpSelector,
  hideMetrics,
  columnVisibilityModel: columnVisibilityModelProp,
  setColumnVisibilityModel,
  pinModel,
  setPinModel,
  filterModel,
  setFilterModel,
  sortModel,
  setSortModel,
  paginationModel,
  setPaginationModel,
  expandRefCols: expandRefColsProp,
  setExpandRefCols,
  allowedColumnPatterns,
  defaultHiddenColumnPrefixes = DEFAULT_HIDDEN_COLUMN_PREFIXES,
  currentViewId,
  charts,
  setCharts,
  chartsOpen,
  setChartsOpen,
  onCellClick,
  disabledFilterColumns,
  selectedRowIds,
  initialHeaderDepth = 2,
  datasetSelector: datasetSelectorProp,
  setDatasetSelector: setDatasetSelectorProp,
  evaluationSelector: evaluationSelectorProp,
  setEvaluationSelector: setEvaluationSelectorProp,
}) => {
  const {loading: loadingUserInfo, userInfo} = useViewerInfo();

  // Use local state as fallback if chartsOpen prop is not provided
  const [localChartsOpen, setLocalChartsOpen] = useState(false);
  const isMetricsChecked = chartsOpen ?? localChartsOpen;
  const setMetricsChecked = setChartsOpen ?? setLocalChartsOpen;

  const isReadonly =
    loadingUserInfo || !userInfo?.username || !userInfo?.teams.includes(entity);

  // Setup Ref to underlying table
  const apiRef = useGridApiRef();

  // Table State consists of:
  // 1. Filter (Structured Filter)
  // 2. Filter (Unstructured Filter)
  // 3. Sort
  // 4. Pagination
  // 5. Expansion
  //
  // The following sections set up the state for these aspects

  // 1. Filter (Structured Filter)
  // Make sure we respect the controlling nature of the filter
  const [filter, setFilter] = useControllableState(
    initialFilter ?? {},
    onFilterUpdate
  );
  // Calculate the effective filter
  const effectiveFilter = useMemo(
    () => getEffectiveFilter(filter, frozenFilter),
    [filter, frozenFilter]
  );

  // 2. Filter (Unstructured Filter)
  const filterModelResolved = filterModel ?? DEFAULT_FILTER_CALLS;

  // Identify if current table is the Evaluations view (needed for dataset filter)
  const isEvaluateTable = useCurrentFilterIsEvaluationsFilter(
    effectiveFilter,
    entity,
    project
  );

  // Selectors are stored as name:hash format (or just name for "all versions")
  const selectedDataset = datasetSelectorProp;
  const selectedEvaluation = evaluationSelectorProp;

  // Merge dataset filter into the grid filter used for queries/charts/exports
  const filterModelWithSelectionsResolved = useMemo(() => {
    if (!isEvaluateTable) {
      return filterModelResolved;
    }
    const items = [...filterModelResolved.items];
    if (selectedDataset) {
      // selectedDataset and selectedEvaluation are in format "name:hash" or just "name"
      // Convert to "object/name:hash" or "object/name:" for filtering
      const filterValue = selectedDataset.includes(':')
        ? `object/${selectedDataset}`
        : `object/${selectedDataset}:`;
      items.push({
        id: 'dataset-filter',
        field: 'inputs.self.dataset',
        operator: '(string): contains',
        value: filterValue,
      } as any);
    }
    if (selectedEvaluation) {
      const filterValue = selectedEvaluation.includes(':')
        ? `object/${selectedEvaluation}`
        : `object/${selectedEvaluation}:`;
      items.push({
        id: 'evaluation-filter',
        field: 'inputs.self',
        operator: '(string): contains',
        value: filterValue,
      } as any);
    }
    return {
      items,
      logicOperator: filterModelResolved.logicOperator,
    } as GridFilterModel;
  }, [
    filterModelResolved,
    isEvaluateTable,
    selectedDataset,
    selectedEvaluation,
  ]);

  const clearFilters = useCallback(() => {
    setFilter({});
    if (setFilterModel) {
      setFilterModel({items: []});
    }
  }, [setFilter, setFilterModel]);

  // 3. Sort
  const sortModelResolved = sortModel ?? DEFAULT_SORT_CALLS;

  // 4. Pagination
  const paginationModelResolved = paginationModel ?? DEFAULT_PAGINATION_CALLS;

  // 5. Expansion
  // Use local state when expand columns props are not provided (uncontrolled mode)
  const [localExpandedRefCols, setLocalExpandedRefCols] = useState<Set<string>>(
    () => new Set<string>()
  );

  const isExpandColsControlled =
    expandRefColsProp !== undefined && setExpandRefCols !== undefined;
  const expandedRefCols = isExpandColsControlled
    ? expandRefColsProp
    : localExpandedRefCols;
  const setExpandedRefColsResolved = isExpandColsControlled
    ? setExpandRefCols
    : setLocalExpandedRefCols;

  // 6. Header Depth Control
  const [maxHeaderDepth, setMaxHeaderDepth] = useState(initialHeaderDepth);

  // 7. Cache for tree building - lifecycle managed by component
  const treeBuildingCacheRef = useRef<TreeBuildingCache>(new Map());

  // Helpers to handle expansion
  const onExpand = (col: string) => {
    const newSet = new Set(expandedRefCols);
    newSet.add(col);
    setExpandedRefColsResolved(newSet);
  };
  const onCollapse = (col: string) => {
    const newSet = new Set(expandedRefCols);
    newSet.delete(col);
    setExpandedRefColsResolved(newSet);
  };

  // Helper to determine if a column is expanded or
  // a child of an expanded column
  const columnIsRefExpanded = useCallback(
    (col: string) => {
      if (expandedRefCols.has(col)) {
        return true;
      }
      for (const refCol of expandedRefCols) {
        if (col.startsWith(refCol + '.')) {
          return true;
        }
      }
      return false;
    },
    [expandedRefCols]
  );

  // Ensure ref expansion for dataset filtering only when needed
  useEffect(() => {
    if (!isEvaluateTable) {
      return;
    }
    if (selectedDataset || selectedEvaluation) {
      if (!expandedRefCols.has('inputs.self')) {
        const newSet = new Set(expandedRefCols);
        newSet.add('inputs.self');
        setExpandedRefColsResolved(newSet);
      }
    }
  }, [
    selectedDataset,
    selectedEvaluation,
    isEvaluateTable,
    expandedRefCols,
    setExpandedRefColsResolved,
  ]);

  const shouldIncludeTotalStorageSize = effectiveFilter.traceRootsOnly;
  const currentViewIdResolved = currentViewId ?? '';

  // Track whether we've already added default expand columns for the current filter
  const defaultsAddedRef = useRef(false);
  // Reset the flag when filter changes
  useEffect(() => {
    defaultsAddedRef.current = false;
  }, [effectiveFilter]);

  // Fetch the calls
  const calls = useCallsForQuery({
    entity,
    project,
    filter: effectiveFilter,
    gridFilter: filterModelWithSelectionsResolved,
    gridPage: paginationModelResolved,
    gridSort: sortModelResolved,
    expandColumns: expandedRefCols,
    columns: undefined,
    options: {
      // The total storage size only makes sense for traces,
      // and not for calls.
      includeTotalStorageSize: shouldIncludeTotalStorageSize,
    },
  });

  // Here, we only update our local state once the calls have loaded.
  // If we were not to do this, we would see a flicker of an empty table
  // before the calls are loaded. Since the columns are data-driven, this
  // flicker also includes the columns disappearing and reappearing. This
  // is not ideal. Instead, we wait for the calls to load, and then update
  // our local state with the new data. The MUI data grid will show the "old"
  // data (perhaps between a page / filter / sort change) until the new data
  // is available. However, since we pass the loading state to the MUI data
  // grid, it will show a loading spinner in the meantime over the old data.
  const callsLoading = calls.metadataLoading;
  const [callsResult, setCallsResult] = useState(calls.result);
  const [callsTotal, setCallsTotal] = useState(calls.total);
  const callsEffectiveFilter = useRef(effectiveFilter);
  const prevViewIdRef = useRef(currentViewIdResolved);
  // A structural change is one where we don't want the set of columns to persist.
  // This can be because of a view change or effective filter (e.g. selected Op) change.
  const hasStructuralChange =
    callsEffectiveFilter.current !== effectiveFilter ||
    prevViewIdRef.current !== currentViewIdResolved;
  useEffect(() => {
    if (hasStructuralChange) {
      setCallsResult([]);
      setCallsTotal(0);
      callsEffectiveFilter.current = effectiveFilter;
      prevViewIdRef.current = currentViewIdResolved;
      // Refetch the calls IFF the filter has changed, this is a
      // noop if the calls query is already loading, but if the filter
      // has no effective impact (frozen vs. not frozen) we need to
      // manually refetch
      calls.refetch();
    } else if (!calls.metadataLoading) {
      setCallsResult(calls.result);
      setCallsTotal(calls.total);
      callsEffectiveFilter.current = effectiveFilter;
      prevViewIdRef.current = currentViewIdResolved;
    }
  }, [calls, effectiveFilter, currentViewIdResolved, hasStructuralChange]);

  // Construct Flattened Table Data
  const tableData: FlattenedCallData[] = useMemo(
    () =>
      prepareFlattenedCallDataForTable(hasStructuralChange ? [] : callsResult),
    [callsResult, hasStructuralChange]
  );

  // This is a specific helper that is used when the user attempts to option-click
  // a cell that is a child cell of an expanded ref. In this case, we want to
  // add a filter on the parent ref itself, not the child cell. Once we can properly
  // filter by reffed values on the backend, this can be removed.
  const getFieldAndValueForRefExpandedFilter = useCallback(
    (field: string, rowId: string) => {
      if (columnIsRefExpanded(field)) {
        // In this case, we actually just want to filter by the parent ref itself.
        // This means we need to:
        // 1. Determine the column of the highest level ancestor column with a ref
        // 2. Get the value of that corresponding cell (ref column @ row)
        // 3. Add a filter for that ref on that column.
        // The acknowledge drawback of this approach is that we are not filtering by that
        // cell's value, but rather the entire object itself. This still might confuse users,
        // but is better than returning nothing.
        const fieldParts = field.split('.');
        let ancestorField: string | null = null;
        let targetRef: string | null = null;
        for (let i = 1; i <= fieldParts.length; i++) {
          const ancestorFieldCandidate = fieldParts.slice(0, i).join('.');
          if (expandedRefCols.has(ancestorFieldCandidate)) {
            const candidateRow = callsResult.find(
              row => row.traceCall.id === rowId
            )?.traceCall;
            if (candidateRow != null) {
              const flattenedCandidateRow =
                flattenObjectPreservingWeaveTypes(candidateRow);
              const targetRefCandidate =
                flattenedCandidateRow[
                  ancestorFieldCandidate + '.' + EXPANDED_REF_REF_KEY
                ];
              if (targetRefCandidate != null) {
                ancestorField = ancestorFieldCandidate;
                targetRef = targetRefCandidate;
                break;
              }
            }
          }
        }
        if (ancestorField == null) {
          console.warn('Could not find ancestor ref column for', field);
          return null;
        }

        return {value: targetRef, field: ancestorField};
      }
      return null;
    },
    [callsResult, columnIsRefExpanded, expandedRefCols]
  );

  const onUpdateFilter: OnUpdateFilter | undefined =
    filterModel && setFilterModel
      ? (field: string, operator: string | null, value: any, rowId: string) => {
          // This condition is used to filter by the parent ref itself, not the child cell.
          // Should be removed once we can filter by reffed values on the backend.
          const expandedRef = getFieldAndValueForRefExpandedFilter(
            field,
            rowId
          );
          const op = operator ?? getDefaultOperatorForValue(value);
          if (expandedRef != null) {
            const newSet = new Set(expandedRefCols);
            newSet.add(expandedRef.field);
            setExpandedRefColsResolved(newSet);
          }

          // All values added to the filter model should be strings, we
          // only allow text-field input in the filter bar (even for numeric)
          // They are converted during the backend mongo-style filter creation
          let strVal: string;
          if (typeof value !== 'string') {
            strVal = JSON.stringify(value);
          } else {
            strVal = value;
          }

          // Check if there is an exact match for field, operator, and value in filterModel.items
          // If an exact match exists, remove it instead of adding a duplicate.
          const existingFullMatchIndex = filterModel.items.findIndex(
            item =>
              item.field === field &&
              item.operator === op &&
              item.value === strVal
          );
          if (existingFullMatchIndex !== -1) {
            const newItems = [...filterModel.items];
            newItems.splice(existingFullMatchIndex, 1);
            setFilterModel({
              ...filterModel,
              items: newItems,
            });
            return;
          }

          // Check if there is a match for field and operator in filterModel.items
          // If a match exists, update the value instead of adding a new filter
          const existingFieldOpMatchIndex = filterModel.items.findIndex(
            item => item.field === field && item.operator === op
          );
          if (existingFieldOpMatchIndex !== -1) {
            const newItems = [...filterModel.items];
            newItems[existingFieldOpMatchIndex] = {
              ...newItems[existingFieldOpMatchIndex],
              value: strVal,
            };
            setFilterModel({
              ...filterModel,
              items: newItems,
            });
            return;
          }

          // There is no match, add a new filter.
          const newModel = {
            ...filterModel,
            items: [
              ...filterModel.items,
              {
                id: getNextFilterId(filterModel.items),
                field,
                operator: op,
                value: strVal,
              },
            ],
          };
          setFilterModel(newModel);
        }
      : undefined;

  // Column Management: Build the columns needed for the table
  const {columns, setUserDefinedColumnWidths, columnsWithRefs} =
    useCallsTableColumns(
      entity,
      project,
      effectiveFilter,
      currentViewIdResolved,
      tableData,
      expandedRefCols,
      onCollapse,
      onExpand,
      columnIsRefExpanded,
      allowedColumnPatterns,
      onUpdateFilter,
      calls.costsLoading,
      !!calls.costsError,
      shouldIncludeTotalStorageSize,
      shouldIncludeTotalStorageSize && calls.storageSizeLoading,
      !!calls.storageSizeError,
      maxHeaderDepth,
      treeBuildingCacheRef.current,
      MAX_COLUMNS,
      calls.heavyIOLoading
    );

  // Now, there are 4 primary controls:
  // 1. Op Version
  // 2. Input Object Version
  // 3. Output Object Version
  // 4. Parent ID
  //
  // The following chunks of code are responsible for determining the
  // values for the options as well as the selected values for each of
  // these controls. They each follow the pattern of:
  //
  // const control = useControlOptions(...)
  // const selectedControl = useMemo(() => {...}, [...])
  //

  // 1. Op Version
  const opVersionOptions = useOpVersionOptions(
    entity,
    project,
    effectiveFilter
  );
  const selectedOpVersionOption = useMemo(() => {
    const opVersionRef = effectiveFilter.opVersionRefs?.[0] ?? null;
    return opVersionRef ?? ALL_TRACES_OR_CALLS_REF_KEY;
  }, [effectiveFilter.opVersionRefs]);

  // 2. Input Object Version
  const inputObjectVersionOptions =
    useInputObjectVersionOptions(effectiveFilter);
  const selectedInputObjectVersion = useMemo(() => {
    const inputObjectVersionRef =
      effectiveFilter.inputObjectVersionRefs?.[0] ?? null;
    return inputObjectVersionRef
      ? inputObjectVersionOptions[inputObjectVersionRef]
      : null;
  }, [inputObjectVersionOptions, effectiveFilter.inputObjectVersionRefs]);

  // 3. Output Object Version
  const outputObjectVersionOptions =
    useOutputObjectVersionOptions(effectiveFilter);
  const selectedOutputObjectVersion = useMemo(() => {
    const outputObjectVersionRef =
      effectiveFilter.outputObjectVersionRefs?.[0] ?? null;
    return outputObjectVersionRef
      ? outputObjectVersionOptions[outputObjectVersionRef]
      : null;
  }, [effectiveFilter.outputObjectVersionRefs, outputObjectVersionOptions]);

  // 4. Parent ID - UI delegated to ParentFilterTag
  const onSetParentFilter = useCallback(
    (parentId: string | undefined) => {
      setFilter({
        ...filter,
        parentId,
      });
    },
    [setFilter, filter]
  );

  const onClearCallIds = useCallback(() => {
    setFilter({
      ...filter,
      callIds: undefined,
    });
  }, [setFilter, filter]);

  // Detect peek drawer state to flip proportions
  const peekLocation = usePeekLocation();
  const isPeekOpen = peekLocation != null;

  // DataGrid Model Management
  const pinModelResolved = pinModel ?? DEFAULT_PIN_CALLS;

  // END OF CPR FACTORED CODE

  // CPR (Tim) - (GeneralRefactoring): Preferably this is passed in from the top, not
  // something where we inspect URLs deep in a component. At the
  // very least this should be moved to it's own function
  // Highlight table row if it matches peek drawer.
  const query = useURLSearchParamsDict();
  const {peekPath} = query;
  const peekId = getPeekId(peekPath);
  const rowIds = useMemo(() => {
    return tableData.map(row => row.__traceCall__.id);
  }, [tableData]);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  useEffect(() => {
    // If selection is controlled externally, don't override it from peek
    if (selectedRowIds !== undefined) {
      return;
    }
    if (rowIds.length === 0) {
      // Data may have not loaded
      return;
    }
    if (peekId == null) {
      // No peek drawer, clear any selection
      setRowSelectionModel([]);
    } else {
      // If peek drawer matches a row, select it.
      // If not, don't modify selection.
      if (rowIds.includes(peekId)) {
        setRowSelectionModel([peekId]);
      }
    }
  }, [rowIds, peekId, selectedRowIds]);

  // Resolve row selection model with controlled override if provided
  // this avoid re-rendering if the rowSelectionModel is set in the effect above
  const rowSelectionModelResolved = useMemo(() => {
    if (selectedRowIds !== undefined) {
      // Only keep ids that are present in current rows
      const filtered = selectedRowIds.filter(id => rowIds.includes(id));
      return filtered as GridRowSelectionModel;
    }
    return rowSelectionModel;
  }, [selectedRowIds, rowIds, rowSelectionModel]);

  const {setRowIds} = useContext(TableRowSelectionContext);
  const {isPeeking} = useContext(WeaveflowPeekContext);
  useEffect(() => {
    if (!isPeeking && setRowIds) {
      setRowIds(rowIds);
    }
  }, [rowIds, isPeeking, setRowIds]);

  // CPR (Tim) - (GeneralRefactoring): Remove this, and add a slot for empty content that can be calculated
  // in the parent component

  const columnVisibilityModel = useMemo(() => {
    // Always use the default column visibility behavior, regardless of metrics state
    if (!columnVisibilityModelProp) {
      return undefined;
    }

    // When peek drawer is open and metrics are showing, only show the trace column and checkbox
    if (isPeekOpen && isMetricsChecked) {
      const visibilityModel: Record<string, boolean> = {};
      columns.cols.forEach(col => {
        if (
          col.field === TRACE_NAME_FIELD ||
          col.field === CUSTOM_CHECKBOX_FIELD
        ) {
          visibilityModel[col.field] = true;
        } else {
          visibilityModel[col.field] = false;
        }
      });
      return visibilityModel;
    }

    const hiddenColumns: string[] = [];
    for (const hiddenColPrefix of defaultHiddenColumnPrefixes) {
      const cols = columns.cols.filter(col =>
        col.field.startsWith(hiddenColPrefix)
      );
      hiddenColumns.push(...cols.map(col => col.field));
    }

    const hiddenColumnVisibilityFalse = hiddenColumns.reduce(
      (acc, col) => {
        // Only add columns=false when not already in the model
        if (columnVisibilityModelProp[col] === undefined) {
          acc[col] = false;
        }
        return acc;
      },
      {} as Record<string, boolean>
    );

    return {
      ...columnVisibilityModelProp,
      ...hiddenColumnVisibilityFalse,
    };
  }, [
    columns.cols,
    columnVisibilityModelProp,
    isPeekOpen,
    isMetricsChecked,
    defaultHiddenColumnPrefixes,
  ]);

  // Selection Management
  const [selectedCalls, setSelectedCalls] = useState<string[]>([]);
  const lastClickedIndexRef = useRef<number | null>(null);
  const clearSelectedCalls = useCallback(() => {
    setSelectedCalls([]);
    lastClickedIndexRef.current = null;
  }, [setSelectedCalls]);

  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate initial table width based on peek drawer state
  const getInitialTableWidth = useCallback(() => {
    if (isPeekOpen) {
      return DEFAULT_TABLE_WIDTH_WHEN_PEEK_OPEN_PX;
    }
    // When peek is closed, we want charts to be ~300px, so table takes the rest
    // Start with a reasonable default - will be adjusted by the effect below
    return Math.max(600, 1200 - DEFAULT_CHARTS_WIDTH_PX - 50); // Assume ~1200px container initially
  }, [isPeekOpen]);

  const [tableWidthPx, setTableWidthPx] = useState(() =>
    getInitialTableWidth()
  );

  // Update table width when peek drawer state changes
  useEffect(() => {
    if (containerRef.current && isMetricsChecked) {
      const containerWidth = containerRef.current.clientWidth;
      if (isPeekOpen) {
        // When peek is open, set table to ~300px
        setTableWidthPx(DEFAULT_TABLE_WIDTH_WHEN_PEEK_OPEN_PX);
      } else {
        // When peek is closed, give charts ~300px, table gets the rest
        const newTableWidth = Math.max(
          TABLE_MIN_WIDTH_PX,
          containerWidth - DEFAULT_CHARTS_WIDTH_PX - 20 // 20px for handle/margins
        );
        setTableWidthPx(newTableWidth);
      }
    }
  }, [isPeekOpen, isMetricsChecked]);

  // Clear selections when switching table types
  useEffect(() => {
    clearSelectedCalls();
  }, [isEvaluateTable, clearSelectedCalls]);

  const muiColumns = useMemo(() => {
    const cols: GridColDef[] = [
      {
        minWidth: 30,
        width: 34,
        field: CUSTOM_CHECKBOX_FIELD,
        sortable: false,
        disableColumnMenu: true,
        resizable: false,
        disableExport: true,
        display: 'flex',
        renderHeader: () => {
          return (
            <Checkbox
              size="small"
              checked={
                selectedCalls.length === 0
                  ? false
                  : selectedCalls.length === tableData.length
                    ? true
                    : 'indeterminate'
              }
              onCheckedChange={() => {
                if (
                  selectedCalls.length ===
                  Math.min(tableData.length, MAX_SELECT)
                ) {
                  setSelectedCalls([]);
                } else {
                  setSelectedCalls(
                    tableData
                      .map(row => row.__traceCall__.id)
                      .slice(0, MAX_SELECT)
                  );
                }
                // Clear the last clicked index when using the header checkbox
                lastClickedIndexRef.current = null;
              }}
            />
          );
        },
        renderCell: (params: any) => {
          const rowId = params.id as string;
          const isSelected = selectedCalls.includes(rowId);
          const disabled = !isSelected && selectedCalls.length >= MAX_SELECT;
          const tooltipText =
            selectedCalls.length >= MAX_SELECT && !isSelected
              ? `Selection limited to ${MAX_SELECT} items`
              : '';

          const handleCheckboxClick = (
            event: React.MouseEvent<HTMLSpanElement>
          ) => {
            if (disabled) {
              return;
            }

            // This is probably fine for these tables (max size is 100)
            // If we were looking at 1000s we could memo a map of id to index, but I left like this for simplicity
            const currentIndex = tableData.findIndex(
              row => row.__traceCall__.id === rowId
            );

            // Handle shift-click for range selection
            if (
              event.shiftKey &&
              lastClickedIndexRef.current !== null &&
              currentIndex !== -1
            ) {
              event.preventDefault();
              const startIndex = Math.min(
                lastClickedIndexRef.current,
                currentIndex
              );
              const endIndex = Math.max(
                lastClickedIndexRef.current,
                currentIndex
              );

              // Get all row IDs in the range
              const rangeIds = tableData
                .slice(startIndex, endIndex + 1)
                .map(row => row.__traceCall__.id);

              // Determine if we should select or deselect based on the anchor row
              const anchorRowId =
                tableData[lastClickedIndexRef.current]?.__traceCall__.id;
              const shouldSelect =
                anchorRowId && selectedCalls.includes(anchorRowId);

              if (shouldSelect) {
                // Add range to selection, respecting MAX_SELECT limit
                const newSelection = Array.from(
                  new Set([...selectedCalls, ...rangeIds])
                ).slice(0, MAX_SELECT);
                setSelectedCalls(newSelection);
              } else {
                // Remove range from selection
                setSelectedCalls(
                  selectedCalls.filter(id => !rangeIds.includes(id))
                );
              }
            } else {
              // Normal click behavior
              if (isSelected) {
                setSelectedCalls(selectedCalls.filter(id => id !== rowId));
              } else {
                setSelectedCalls([...selectedCalls, rowId]);
              }
              lastClickedIndexRef.current = currentIndex;
            }
          };

          return (
            <Tooltip title={tooltipText} placement="right" arrow>
              {/* https://mui.com/material-ui/react-tooltip/ */}
              {/* By default disabled elements like <button> do not trigger user interactions */}
              {/* To accommodate disabled elements, add a simple wrapper element, such as a span. */}
              <span
                onClick={e => {
                  e.stopPropagation();
                  handleCheckboxClick(e);
                }}>
                <Checkbox
                  size="small"
                  disabled={disabled}
                  checked={isSelected}
                  onCheckedChange={() => {
                    // No-op: All selection logic handled by span onClick to access shift key
                  }}
                />
              </span>
            </Tooltip>
          );
        },
      },
      ...columns.cols.map(col => {
        // When peek drawer is open and metrics are showing, make the trace column take full width
        if (isPeekOpen && isMetricsChecked && col.field === TRACE_NAME_FIELD) {
          return {
            ...col,
            flex: 1,
            minWidth: 250,
          };
        }
        return col;
      }),
    ];
    return cols;
  }, [columns.cols, selectedCalls, tableData, isPeekOpen, isMetricsChecked]);

  // MUI data grid is unhappy if you pass it a sort model
  // that references columns that aren't in the grid - it triggers an
  // infinite loop.
  const sortModelFiltered = useMemo(() => {
    // Get all valid column fields from muiColumns
    const validColumnFields = new Set(muiColumns.map(col => col.field));

    // Filter out any sort items that reference columns not in muiColumns
    return sortModelResolved.filter(sortItem =>
      validColumnFields.has(sortItem.field)
    );
  }, [muiColumns, sortModelResolved]);

  // Register Compare Evaluations Button
  const history = useHistory();
  const router = useWeaveflowCurrentRouteContext();

  // We really want to use columns here, but because visibleColumns
  // is a prop to ExportSelector, it causes infinite reloads.
  // memoize key computation, then filter out hidden columns
  const allRowKeys = useMemo(() => {
    const keysSet = new Set<string>();
    tableData.forEach(row => {
      Object.keys(row).forEach(key => keysSet.add(key));
    });
    // `storage_size_bytes` is never shown in the table view, so don't include it
    keysSet.delete('storage_size_bytes');
    // set the `total_storage_size_bytes` based on whether we are showing trace roots only
    if (!effectiveFilter.traceRootsOnly) {
      keysSet.delete('total_storage_size_bytes');
    }

    return Array.from(keysSet);
  }, [tableData, effectiveFilter]);

  const visibleColumns = useMemo(() => {
    return tableData.length > 0
      ? allRowKeys.filter(col => columnVisibilityModel?.[col] !== false)
      : [];
  }, [allRowKeys, columnVisibilityModel, tableData]);

  // Add default columns only once per filter configuration
  useEffect(() => {
    if (
      !callsLoading &&
      columnsWithRefs.size > 0 &&
      !defaultsAddedRef.current
    ) {
      let needsUpdate = false;
      const updatedCols = new Set(expandedRefCols);

      DEFAULT_EXPAND_CALLS.forEach(defaultCol => {
        if (
          columnsWithRefs.has(defaultCol) &&
          !expandedRefCols.has(defaultCol)
        ) {
          updatedCols.add(defaultCol);
          needsUpdate = true;
        }
      });

      if (needsUpdate) {
        defaultsAddedRef.current = true;
        setExpandedRefColsResolved(updatedCols);
      }
    }
  }, [
    callsLoading,
    columnsWithRefs,
    expandedRefCols,
    setExpandedRefColsResolved,
  ]);

  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [addToDatasetModalOpen, setAddToDatasetModalOpen] = useState(false);

  // Called in reaction to Hide column menu

  const onPinnedColumnsChange = useCallback(
    (newModel: GridPinnedColumnFields) => {
      if (!setPinModel || callsLoading) {
        return;
      }
      setPinModel(newModel);
    },
    [callsLoading, setPinModel]
  );

  const onSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      if (!setSortModel || callsLoading) {
        return;
      }
      // The Grid calls this function when the columns change, removing
      // sort items whose field is no longer in the columns. However, the user
      // might have been sorting on an output, and the output columns might
      // not have been determined yet. So skip setting the sort model in this case.
      if (!muiColumns.some(col => col.field.startsWith('output'))) {
        return;
      }

      // handle feedback conversion from weave summary to backend filter
      for (const sort of newModel) {
        if (sort.field.startsWith('summary.weave.feedback')) {
          const parsed = parseFeedbackType(sort.field);
          if (parsed) {
            const backendFilter = convertFeedbackFieldToBackendFilter(
              parsed.field
            );
            sort.field = backendFilter;
          }
        }
      }
      setSortModel(newModel);
    },
    [callsLoading, setSortModel, muiColumns]
  );

  const onPaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      if (!setPaginationModel || callsLoading) {
        return;
      }
      setPaginationModel(newModel);
    },
    [callsLoading, setPaginationModel]
  );

  const noRowsOverlay = useCallback(() => {
    if (calls.primaryError) {
      return (
        <ErrorPanel
          title="Oh no! Unable to load traces..."
          error={calls.primaryError}
        />
      );
    }
    return (
      <CallsTableNoRowsOverlay
        entity={entity}
        project={project}
        callsLoading={callsLoading}
        callsResult={callsResult}
        isEvaluateTable={isEvaluateTable}
        effectiveFilter={effectiveFilter}
        filterModelResolved={filterModelWithSelectionsResolved}
        clearFilters={clearFilters}
        setFilterModel={setFilterModel}
      />
    );
  }, [
    calls.primaryError,
    callsLoading,
    callsResult,
    clearFilters,
    effectiveFilter,
    filterModelWithSelectionsResolved,
    isEvaluateTable,
    setFilterModel,
    entity,
    project,
  ]);

  // Common StyledDataGrid configuration
  const dataGridProps = {
    // Start Column Menu
    // ColumnMenu is needed to support pinning and column visibility
    disableColumnMenu: false,
    // ColumnFilter is definitely useful
    disableColumnFilter: true,
    disableMultipleColumnsFiltering: false,
    // ColumnPinning seems to be required in DataGridPro, else it crashes.
    // However, in this case it is also useful.
    disableColumnPinning: false,
    // ColumnReorder is definitely useful
    // TODO (Tim): This needs to be managed externally (making column
    // ordering a controlled property) This is a "regression" from the calls
    // table refactor
    disableColumnReorder: true,
    // ColumnResize is definitely useful
    disableColumnResize: false,
    // ColumnSelector is definitely useful
    disableColumnSelector: false,
    disableMultipleColumnsSorting: true,
    // End Column Menu
    columnHeaderHeight: 40,
    apiRef,
    loading: callsLoading,
    rows: tableData,
    // initialState={initialState}
    onColumnVisibilityModelChange: setColumnVisibilityModel
      ? (newModel: GridColumnVisibilityModel) => {
          setColumnVisibilityModel(newModel);
        }
      : undefined,
    columnVisibilityModel,
    // SORT SECTION START
    sortingMode: 'server' as const,
    sortModel: sortModelFiltered,
    onSortModelChange,
    // SORT SECTION END
    // PAGINATION SECTION START
    pagination: true,
    rowCount: calls.primaryError ? 0 : callsTotal,
    paginationMode: 'server' as const,
    paginationModel,
    onPaginationModelChange,
    // PAGINATION SECTION END
    rowHeight: 38,
    columns: muiColumns,
    disableRowSelectionOnClick: true,
    rowSelectionModel: rowSelectionModelResolved,
    // columnGroupingModel={groupingModel}
    columnGroupingModel: columns.colGroupingModel,
    hideFooter: !callsLoading && callsTotal === 0,
    hideFooterSelectedRowCount: true,
    onColumnWidthChange: (newCol: any) => {
      setUserDefinedColumnWidths(curr => {
        return {
          ...curr,
          [newCol.colDef.field]: newCol.colDef.computedWidth,
        };
      });
    },
    pinnedColumns: pinModelResolved,
    onPinnedColumnsChange,
    slots: {
      noRowsOverlay,
      columnMenu: CallsCustomColumnMenu,
      pagination: () => (
        <PaginationButtons
          hideControls={hideControls}
          isEvaluateTable={isEvaluateTable}
        />
      ),
      columnMenuSortDescendingIcon: IconSortDescending,
      columnMenuSortAscendingIcon: IconSortAscending,
      columnMenuHideIcon: IconNotVisible,
      columnMenuPinLeftIcon: () => (
        <IconPinToRight style={{transform: 'scaleX(-1)'}} />
      ),
      columnMenuPinRightIcon: IconPinToRight,
      loadingOverlay: CustomLoadingOverlay,
    },
    className: 'tw-style',
    onCellClick,
  };

  // CPR (Tim) - (GeneralRefactoring): Pull out different inline-properties and create them above

  return (
    <FilterLayoutTemplate
      filterListSx={{
        pb: 1,
        display: hideControls ? 'none' : 'flex',
        alignItems: 'center',
      }}
      filterListItems={
        <TailwindContents>
          {selectedCalls.length === 0 ? (
            <>
              <RefreshButton
                onClick={() => calls.refetch()}
                disabled={callsLoading}
              />
              {/* Placeholder button for compare until items are selected in table. */}
              {isEvaluateTable && (
                <CompareEvaluationsTableButton
                  onClick={() => {}}
                  disabled={true}
                  tooltipText="Select evaluations to compare"
                  text="Compare"
                />
              )}
              {!hideOpSelector && (
                <OpSelector
                  frozenFilter={frozenFilter}
                  filter={filter}
                  setFilter={setFilter}
                  selectedOpVersionOption={selectedOpVersionOption}
                  opVersionOptions={opVersionOptions}
                  useMenuPortalBody={true}
                  width="320px"
                />
              )}

              {isEvaluateTable && (
                <>
                  <ObjectVersionSelector
                    objectClass="Evaluation"
                    value={selectedEvaluation ?? null}
                    onChange={setEvaluationSelectorProp ?? (() => {})}
                    icon={<IconBaselineAlt />}
                    placeholder="Select an eval"
                  />
                  <ObjectVersionSelector
                    objectClass="Dataset"
                    value={selectedDataset ?? null}
                    onChange={setDatasetSelectorProp ?? (() => {})}
                    icon={<IconTable />}
                    placeholder="Select a dataset"
                  />
                </>
              )}
              {filterModel && setFilterModel && (
                <FilterPanel
                  entity={entity}
                  project={project}
                  filterModel={filterModel}
                  columnInfo={columns}
                  setFilterModel={setFilterModel}
                  selectedCalls={effectiveFilter.callIds || []}
                  clearSelectedCalls={onClearCallIds}
                  disabledFilterColumns={disabledFilterColumns}
                  userId={userInfo && 'id' in userInfo ? userInfo.id : null}
                />
              )}
            </>
          ) : (
            <div className="flex items-center gap-8">
              <Button
                variant="ghost"
                size="small"
                icon="close"
                onClick={() => setSelectedCalls([])}
                tooltip="Clear selection"
              />
              <div className="text-sm">
                {selectedCalls.length}{' '}
                {isEvaluateTable
                  ? selectedCalls.length === 1
                    ? 'evaluation'
                    : 'evaluations'
                  : selectedCalls.length === 1
                    ? 'trace'
                    : 'traces'}{' '}
                selected:
              </div>
              {isEvaluateTable ? (
                <CompareEvaluationsTableButton
                  text={selectedCalls.length === 1 ? 'View' : 'Compare'}
                  tooltipText={
                    selectedCalls.length === 1
                      ? 'Select 2 or more evaluations to compare'
                      : 'Compare metrics and examples for selected evaluations'
                  }
                  onClick={() => {
                    history.push(
                      router.compareEvaluationsUri(
                        entity,
                        project,
                        selectedCalls,
                        null,
                        null
                      )
                    );
                  }}
                />
              ) : (
                <CompareTracesTableButton
                  onClick={() => {
                    history.push(
                      router.compareCallsUri(entity, project, selectedCalls)
                    );
                  }}
                  disabled={selectedCalls.length < 2}
                />
              )}
              {!isReadonly && (
                <>
                  <div className="flex-none">
                    <BulkAddToDatasetButton
                      onClick={() => setAddToDatasetModalOpen(true)}
                      disabled={selectedCalls.length === 0}
                    />
                    {addToDatasetModalOpen && (
                      <AddToDatasetDrawer
                        entity={entity}
                        project={project}
                        open={true}
                        onClose={() => setAddToDatasetModalOpen(false)}
                        selectedCallIds={selectedCalls}
                      />
                    )}
                  </div>
                  {setFilter && (
                    <div className="flex-none">
                      <FilterSelectedButton
                        selectedCalls={selectedCalls}
                        filter={filter}
                        setFilter={setFilter}
                        disabled={selectedCalls.length === 0}
                        clearSelectedCalls={clearSelectedCalls}
                      />
                    </div>
                  )}
                  <div className="flex-none">
                    <BulkDeleteButton
                      onClick={() => setDeleteConfirmModalOpen(true)}
                      disabled={selectedCalls.length === 0}
                    />
                    <ConfirmDeleteModal
                      calls={tableData
                        .filter(row =>
                          selectedCalls.includes(row.__traceCall__.id)
                        )
                        .map(row => traceCallToUICallSchema(row.__traceCall__))}
                      confirmDelete={deleteConfirmModalOpen}
                      setConfirmDelete={setDeleteConfirmModalOpen}
                      onDeleteCallback={() => {
                        setSelectedCalls([]);
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <div className="ml-auto min-w-[112px] flex min-w-0 items-center gap-8 overflow-hidden">
            {selectedInputObjectVersion && (
              <RemovableTag
                color="moon"
                label={`Input: ${objectVersionNiceString(
                  selectedInputObjectVersion
                )}`}
                removeAction={
                  <RemoveAction
                    onClick={(e: React.SyntheticEvent) => {
                      e.stopPropagation();
                      setFilter({
                        ...filter,
                        inputObjectVersionRefs: undefined,
                      });
                    }}
                  />
                }
              />
            )}
            {selectedOutputObjectVersion && (
              <RemovableTag
                color="moon"
                label={`Output: ${objectVersionNiceString(
                  selectedOutputObjectVersion
                )}`}
                removeAction={
                  <RemoveAction
                    onClick={(e: React.SyntheticEvent) => {
                      e.stopPropagation();
                      setFilter({
                        ...filter,
                        outputObjectVersionRefs: undefined,
                      });
                    }}
                  />
                }
              />
            )}
            <ParentFilterTag
              entity={entity}
              project={project}
              parentId={effectiveFilter.parentId}
              onSetParentFilter={onSetParentFilter}
            />
            <div className="flex-none">
              <ExportSelector
                selectedCalls={selectedCalls}
                numTotalCalls={callsTotal}
                visibleColumns={visibleColumns}
                refColumnsToExpand={Array.from(expandedRefCols).filter(col =>
                  visibleColumns.includes(col)
                )}
                callQueryParams={{
                  entity,
                  project,
                  filter: effectiveFilter,
                  gridFilter: filterModelWithSelectionsResolved,
                  gridSort: sortModel,
                }}
              />
            </div>
            {!hideMetrics && (
              <Button
                variant="secondary"
                icon="chart-vertical-bars"
                active={isMetricsChecked}
                onClick={() => setMetricsChecked(!isMetricsChecked)}
                tooltip={isMetricsChecked ? 'Hide metrics' : 'Show metrics'}
                className={`h-32 w-32 ${isPeekOpen ? 'md:h-32 md:w-32' : 'md:h-32 md:w-max'}`}>
                <span className={isPeekOpen ? 'hidden' : 'hidden md:flex'}>
                  Visualize
                </span>
              </Button>
            )}
            {columnVisibilityModel &&
              setColumnVisibilityModel &&
              !(isPeekOpen && isMetricsChecked) && (
                <>
                  <div className="flex-none">
                    <ManageColumnsButton
                      columnInfo={columns}
                      columnVisibilityModel={columnVisibilityModel}
                      setColumnVisibilityModel={setColumnVisibilityModel}
                      headerDepth={maxHeaderDepth}
                      onHeaderDepthChange={setMaxHeaderDepth}
                      minHeaderDepth={1}
                      maxHeaderDepth={8}
                      hideLabel={isPeekOpen}
                      pinModel={pinModel}
                      setPinModel={setPinModel}
                      alwaysPinnedLeft={COLUMNS_ALWAYS_PINNED_LEFT}
                    />
                  </div>
                </>
              )}
          </div>
        </TailwindContents>
      }>
      {isMetricsChecked ? (
        <div
          ref={containerRef}
          style={{
            display: 'flex',
            height: `calc(100vh - ${SPLIT_VIEW_CONTAINER_HEIGHT_OFFSET_PX}px)`,
            minHeight: `${SPLIT_VIEW_CONTAINER_MIN_HEIGHT_PX}px`,
            width: '100%',
            gap: '0px',
            position: 'relative',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}>
          <div
            style={{
              width: `${tableWidthPx}px`,
              minWidth: `${TABLE_MIN_WIDTH_PX}px`,
              height: '100%',
              overflow: 'hidden',
              flexShrink: 0,
              flexGrow: 0,
              boxSizing: 'border-box',
            }}>
            <StyledDataGrid
              {...dataGridProps}
              sx={{
                borderRadius: 0,
                height: '100%',
                width: '100% !important',
                maxWidth: 'none !important',
                minWidth: `${TABLE_MIN_WIDTH_PX}px !important`,
                overflow: 'hidden',
                '& .MuiDataGrid-virtualScroller': {
                  overflowX: 'auto',
                },
                // This moves the pagination controls to the left
                '& .MuiDataGrid-footerContainer': {
                  justifyContent: 'flex-start',
                  minHeight: '40px',
                },
                '& .MuiDataGrid-main:focus-visible': {
                  outline: 'none',
                },
              }}
            />
          </div>
          <ResizableHandle
            containerRef={containerRef}
            onWidthChange={setTableWidthPx}
            minWidth={TABLE_MIN_WIDTH_PX}
            maxWidthOffset={RESIZABLE_HANDLE_MAX_WIDTH_OFFSET_PX}
            widthPx={RESIZE_HANDLE_WIDTH_PX}
          />
          <div
            style={{
              flex: '1',
              minWidth: `${CHARTS_MIN_WIDTH_PX}px`,
              height: '100%',
              overflowX: 'hidden',
              overflowY: 'auto',
              borderTop: '1px solid rgba(224, 224, 224, 1)',
            }}>
            <CallsSummaryProvider calls={callsResult} visibleRowIds={rowIds}>
              <CallsCharts
                entity={entity}
                project={project}
                filter={effectiveFilter}
                filterModelProp={filterModelWithSelectionsResolved}
                sortModel={sortModelResolved}
                charts={charts}
                setCharts={setCharts}
                currentViewId={currentViewId}
                expandRefCols={expandedRefCols}
                isEvaluationPage={isEvaluateTable}
              />
            </CallsSummaryProvider>
          </div>
        </div>
      ) : (
        <StyledDataGrid
          {...dataGridProps}
          sx={{
            borderRadius: 0,
            // This moves the pagination controls to the left
            '& .MuiDataGrid-footerContainer': {
              justifyContent: 'flex-start',
              minHeight: '40px',
            },
            '& .MuiDataGrid-main:focus-visible': {
              outline: 'none',
            },
          }}
        />
      )}
    </FilterLayoutTemplate>
  );
};

// Get the tail of the peekPath (ignore query params)
const getPeekId = (peekPath: string | null): string | null => {
  if (!peekPath) {
    return null;
  }
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const url = new URL(peekPath, baseUrl);
  const {pathname} = url;
  return pathname.split('/').pop() ?? null;
};

export type FlattenedCallData = {
  __traceCall__: TraceCallSchema;
  [key: string]: unknown;
};

export function prepareFlattenedCallDataForTable(
  callsResult: CallSchema[]
): FlattenedCallData[] {
  const traceCalls = callsResult.map(c => c.traceCall);
  const flattened = prepareFlattenedDataForTable(traceCalls);
  return flattened.map((c, i) => ({...c, __traceCall__: traceCalls[i]}));
}
